import { jsx as _jsx } from "react/jsx-runtime";
import { getCoordinateString } from '../../utils/coordinates';
import { gridInsetPercent } from '../../utils/mapGeometry';
import './StartPointPicker.scss';
const sameCell = (a, x, y) => !!a && a.x === x && a.y === y;
// A clickable mini-map: pick the start and goal cells and paint impassable Empty
// cells (walls) visually instead of typing coordinates. Start and goal are mutually
// exclusive with each other and with walls.
function StartPointPicker({ width, height, value, onChange, disabled, mode = 'start', goal = null, onSetGoal, empties = [], onToggleEmpty, routeCells = [], onToggleRoute, backgroundImageUrl = null, }) {
    const cols = Math.max(1, Math.floor(width) || 1);
    const rows = Math.max(1, Math.floor(height) || 1);
    const emptySet = new Set(empties.map(e => `${e.x},${e.y}`));
    const routeSet = new Set(routeCells.map(e => `${e.x},${e.y}`));
    const routeMode = routeCells.length > 0 || mode === 'route';
    const handleClick = (x, y) => {
        if (disabled)
            return;
        if (mode === 'route') {
            // Start and goal are always on the route — don't toggle them off.
            if (sameCell(value, x, y) || sameCell(goal, x, y))
                return;
            onToggleRoute === null || onToggleRoute === void 0 ? void 0 : onToggleRoute({ x, y });
        }
        else if (mode === 'empty') {
            // Neither the start nor the goal cell can ever be a wall.
            if (sameCell(value, x, y) || sameCell(goal, x, y))
                return;
            onToggleEmpty === null || onToggleEmpty === void 0 ? void 0 : onToggleEmpty({ x, y });
        }
        else if (mode === 'goal') {
            // The goal can't sit on the start cell (they're the two endpoints).
            if (sameCell(value, x, y))
                return;
            onSetGoal === null || onSetGoal === void 0 ? void 0 : onSetGoal({ x, y });
        }
        else {
            // Start can't sit on the goal cell.
            if (sameCell(goal, x, y))
                return;
            onChange({ x, y });
        }
    };
    return (_jsx("div", { className: `start-picker ${backgroundImageUrl ? 'has-bg' : ''} ${routeMode ? 'has-route' : ''}`, children: _jsx("div", { className: "sp-stage", style: backgroundImageUrl ? { backgroundImage: `url("${backgroundImageUrl}")` } : undefined, children: _jsx("div", { className: "sp-grid", style: Object.assign({ gridTemplateColumns: `repeat(${cols}, 1fr)`, gridTemplateRows: `repeat(${rows}, 1fr)`, aspectRatio: `${cols} / ${rows}` }, (backgroundImageUrl ? { inset: `${gridInsetPercent(cols)}%` } : {})), children: Array.from({ length: rows }).flatMap((_, y) => Array.from({ length: cols }).map((_, x) => {
                    const isStart = sameCell(value, x, y);
                    const isGoal = sameCell(goal, x, y);
                    const isEmpty = emptySet.has(`${x},${y}`);
                    const isRoute = routeMode && (routeSet.has(`${x},${y}`) || isStart || isGoal);
                    const label = isStart ? 'start' : isGoal ? 'goal'
                        : isRoute ? 'route' : isEmpty ? 'empty (impassable)' : routeMode ? 'off-route' : 'open';
                    return (_jsx("button", { type: "button", className: `sp-cell ${isStart ? 'selected' : ''} ${isGoal ? 'goal' : ''} ${isEmpty ? 'empty' : ''} ${isRoute ? 'route' : ''}`, disabled: disabled, "aria-label": `${getCoordinateString(x, y)} — ${label}`, "aria-pressed": isStart || isGoal || isEmpty, onClick: () => handleClick(x, y), children: isStart ? '★' : isGoal ? '⚑' : '' }, `${x}-${y}`));
                })) }) }) }));
}
export default StartPointPicker;
